import React, { ChangeEvent, useCallback, useContext, useEffect, useState } from 'react';
import {
  MdCalendarMonth,
  MdEmail,
  MdHouse,
  MdLocationCity,
  MdMap,
  MdNote,
  MdNumbers,
  MdPerson,
  MdPhone,
  MdStreetview,
} from 'react-icons/md';
import Skeleton from 'react-loading-skeleton';
import { toast } from 'react-toastify';

import {
  InputAdornment,
  TextField,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  Card,
  CardHeader,
  CardContent,
} from '@mui/material';
import { get, getCEPAddress, update } from 'api/api';
import ErrorMessage from 'components/ErrorMessage';
import LoadingAnimation from 'components/Loader/Loader';
import { useAuth } from 'contexts/auth';
import LoadingContext from 'contexts/loading';
import { UpdateProfileForm, UpdateProfileRequestDTO } from 'dtos/ProfileDTO';
import { useFormik } from 'formik';
import { getUser } from 'services/auth';
import { updateProfileFormSchema } from 'shared/validators';
import { NeedsValidationGrid, Span2 } from 'styles/Styles';
import { AppError } from 'utils/AppError';
import { convertStringToDate } from 'utils/convertStringToDate';
import { handleApiError } from 'utils/handleApiError';
import { maskAddressNumber } from 'utils/MaskAddressNumber';
import { maskCEP } from 'utils/MaskCep';
import { maskCpf } from 'utils/MaskCpf';
import { maskDate } from 'utils/MaskDate';
import { maskName } from 'utils/MaskName';
import { maskPhoneNumber } from 'utils/MaskPhoneNumber';
import { unmaskCEP } from 'utils/UnMaskCep';
import { unmaskCpf } from 'utils/UnMaskCpf';
import { unmaskPhoneNumber } from 'utils/UnMaskPhoneNumber';

import { USER_KEY } from '../../constants';

// TODO: Substituir todas as funções helper, masks e unmasks pelas que estão nestes
// respectivos arquivos no @esteio-gestao/common

const Profile: React.FC = () => {
  const user = getUser();
  const context = useAuth();
  const [isSearchingCep, setIsSearchingCep] = useState(false);
  const [initialValues, setInitialValues] = useState<UpdateProfileForm>({} as UpdateProfileForm);
  const { isLoading: isFechingProfileData, setLoading: setIsFetchingProfileData } =
    useContext(LoadingContext);

  const {
    values,
    errors,
    isSubmitting,
    handleChange,
    setFieldValue,
    handleSubmit,
    setSubmitting,
    resetForm,
  } = useFormik({
    validationSchema: updateProfileFormSchema,
    initialValues,
    onSubmit: (values: UpdateProfileForm) => {
      handleSubmitUpdateProfile(values);
    },
    validateOnChange: false,
    validateOnBlur: false,
  });

  const handleSubmitUpdateProfile = async (fields: UpdateProfileForm) => {
    try {
      if (!!user) {
        const payload: UpdateProfileRequestDTO = {
          nome: fields.fullName.trim(),
          email: fields.email.trim(),
          cpf: unmaskCpf(fields.cpf),
          dataNascimento: convertStringToDate(fields.birthDate),
          sexo: fields.sex,
          telefone: unmaskPhoneNumber(fields.firstPhone),
          // telefone_2: fields.secondPhone ? unmaskPhoneNumber(fields.secondPhone) : undefined,
          cep: unmaskCEP(fields.cep),
          bairro: fields.neighborhood.trim(),
          logradouro: fields.street.trim(),
          complemento: fields.complement && fields.complement.trim(),
          numero: fields.number,
          cidade: fields.city,
          estado: fields.state,
        };

        const { data: updatedProfileData } = await update(
          context.getSlugFromRoleSelected(),
          user.codPessoa,
          payload,
        );

        if (updatedProfileData) toast.success('Dados atualizados com sucesso.');

        const { data } = await get(`user/${context?.user?.id}`);
        if (data) {
          localStorage.setItem(USER_KEY, JSON.stringify(data));
          context.refresh();
        }

        if (initialValues.email !== fields.email) {
          toast.info('Email do usuário alterado, favor logar novamente.');
          await context.logoutUser();
        }
      }
    } catch (err) {
      const error = err as AppError;

      handleApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSearchCep = useCallback(
    async (cep: string) => {
      if (cep.length !== 9) return;

      if (cep) {
        try {
          setIsSearchingCep(true);
          const response = await getCEPAddress(cep);

          if (response) {
            setFieldValue('city', response.data.localidade);
            setFieldValue('state', response.data.uf);
          }
        } catch (err) {
          setFieldValue('city', '');
          setFieldValue('state', '');

          const error = err as AppError;
          toast.error(error.data);
        } finally {
          setIsSearchingCep(false);
        }
      }
    },
    [values?.cep],
  );

  const fetchProfile = useCallback(async () => {
    setIsFetchingProfileData(true);

    try {
      const { data } = await get(`user/${user?.id}`);

      if (data && data.pessoa) {
        const profileObj: UpdateProfileForm = {
          fullName: data?.pessoa?.pessoaFisica?.nome,
          email: data?.email,
          cpf: maskCpf(data?.pessoa?.pessoaFisica?.cpf),
          birthDate: maskDate(data?.pessoa?.pessoaFisica?.dataNascimento),
          sex: data?.pessoa?.pessoaFisica?.sexo,
          firstPhone: maskPhoneNumber(data?.pessoa?.telefone),
          // secondPhone: maskPhoneNumber(data?.pessoa?.telefone_2),
          cep: maskCEP(data?.pessoa?.endereco?.cep),
          street: data?.pessoa?.endereco?.logradouro,
          neighborhood: data?.pessoa?.endereco?.bairro,
          complement: data?.pessoa?.endereco?.complemento,
          number: data?.pessoa?.endereco?.numero,
        };

        resetForm({
          values: profileObj,
        });

        setInitialValues(profileObj);

        await handleSearchCep(maskCEP(data?.pessoa?.endereco?.cep));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsFetchingProfileData(false);
    }
  }, [user, resetForm]);

  const handleSetCPF = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const maskedCpf = maskCpf(e.target.value);
    setFieldValue('cpf', maskedCpf);
    setFieldValue('cnpj', '');
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  return (
    <>
      <Box>
        <Card>
          <CardHeader title="Editar dados de perfil" />

          <CardContent>
            <NeedsValidationGrid
              className="needs-validation-grid"
              onSubmit={handleSubmit}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                }
              }}
            >
              <div className="span-2">
                <TextField
                  id="input-fullName"
                  label="Nome completo"
                  placeholder="Pedro Morais de Oliveira"
                  fullWidth
                  required
                  disabled
                  value={values?.fullName}
                  error={!!errors?.fullName}
                  onChange={(e) => {
                    const maskedName = maskName(e.target.value);
                    setFieldValue('fullName', maskedName);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdPerson />
                      </InputAdornment>
                    ),
                  }}
                />
                {errors?.fullName && <ErrorMessage error={errors?.fullName} />}
              </div>

              <div>
                <TextField
                  id="input-email"
                  type="email"
                  required
                  fullWidth
                  disabled
                  value={values?.email}
                  error={!!errors?.email}
                  onChange={handleChange('email')}
                  placeholder="joao@gmail.com"
                  label="E-mail"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdEmail />
                      </InputAdornment>
                    ),
                  }}
                />
                {errors?.email && <ErrorMessage error={errors?.email} />}
              </div>

              <div>
                <TextField
                  id="input-phone-1"
                  fullWidth
                  required
                  disabled
                  value={values?.firstPhone}
                  error={!!errors?.firstPhone}
                  label="Celular"
                  placeholder="(00) 0 0000-0000"
                  onChange={(e) => {
                    const maskedPhoneNumber = maskPhoneNumber(e.target.value);
                    setFieldValue('firstPhone', maskedPhoneNumber);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdPhone />
                      </InputAdornment>
                    ),
                  }}
                />

                {errors?.firstPhone && <ErrorMessage error={errors?.firstPhone} />}
              </div>

              {initialValues?.cpf && (
                <>
                  <FormControl fullWidth>
                    <InputLabel id="label-sexo">Sexo</InputLabel>
                    <Select
                      id="input-sex"
                      fullWidth
                      required
                      disabled
                      value={values?.sex}
                      error={!!errors?.sex}
                      placeholder="Masculino"
                      label="Sexo"
                      labelId="label-sexo"
                      defaultValue="masculino"
                      onChange={() => handleChange('sex')}
                    >
                      <MenuItem value="sexo" disabled>
                        Selecione seu sexo
                      </MenuItem>
                      <MenuItem value="1">Masculino</MenuItem>
                      <MenuItem value="0">Feminino</MenuItem>
                      {/* <MenuItem value="outro">Outro</MenuItem> */}
                    </Select>
                  </FormControl>
                </>
              )}

              <div>
                <TextField
                  id="input-birthdate"
                  fullWidth
                  required
                  disabled
                  value={values?.birthDate}
                  error={!!errors?.birthDate}
                  onChange={(e) => {
                    const maskedDate = maskDate(e.target.value);
                    setFieldValue('birthDate', maskedDate);
                  }}
                  label="Data de nascimento"
                  placeholder="01/01/1999"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdCalendarMonth />
                      </InputAdornment>
                    ),
                  }}
                />

                {errors?.birthDate && <ErrorMessage error={errors?.birthDate} />}
              </div>

              <div>
                <TextField
                  id="input-cpf"
                  label="CPF"
                  placeholder="599.010.830-36"
                  required
                  disabled
                  fullWidth
                  value={values?.cpf}
                  error={!!errors?.cpf}
                  onChange={handleSetCPF}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdNote />
                      </InputAdornment>
                    ),
                  }}
                />
                {errors?.cpf && <ErrorMessage error={errors?.cpf} />}
              </div>

              {isSearchingCep ? (
                <Skeleton height={56} />
              ) : (
                <div>
                  <TextField
                    id="input-city"
                    fullWidth
                    required
                    disabled
                    value={values?.city}
                    error={!!errors?.city}
                    onChange={handleChange('city')}
                    label="Cidade"
                    placeholder="Belo Horizonte"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdLocationCity />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {errors?.city && <ErrorMessage error={errors?.city} />}
                </div>
              )}

              {isSearchingCep ? (
                <Skeleton height={56} />
              ) : (
                <div>
                  <TextField
                    id="input-state"
                    fullWidth
                    required
                    disabled
                    value={values?.state}
                    error={!!errors?.state}
                    onChange={handleChange('state')}
                    label="Estado (UF)"
                    placeholder="SP"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdMap />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {errors?.state && <ErrorMessage error={errors?.state} />}
                </div>
              )}

              <div>
                <TextField
                  id="input-cep"
                  label="CEP"
                  placeholder="31234-567"
                  required
                  disabled
                  fullWidth
                  value={values?.cep}
                  error={!!errors?.cep}
                  onChange={(e) => {
                    const maskedCep = maskCEP(e.target.value);
                    setFieldValue('cep', maskedCep);
                    e.target.value.length === 9 && handleSearchCep(e.target.value);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdHouse />
                      </InputAdornment>
                    ),
                  }}
                />
                {errors?.cep && <ErrorMessage error={errors?.cep} />}
              </div>

              <div>
                <TextField
                  id="input-neighborhood"
                  fullWidth
                  required
                  disabled
                  value={values?.neighborhood}
                  error={!!errors?.neighborhood}
                  onChange={handleChange('neighborhood')}
                  label="Bairro"
                  placeholder="São Joaquim"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdLocationCity />
                      </InputAdornment>
                    ),
                  }}
                />
                {errors?.neighborhood && <ErrorMessage error={errors?.neighborhood} />}
              </div>

              <div>
                <TextField
                  id="input-street"
                  fullWidth
                  required
                  disabled
                  value={values?.street}
                  error={!!errors?.street}
                  onChange={handleChange('street')}
                  label="Logradouro"
                  placeholder="Rua Maria Amélia"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdStreetview />
                      </InputAdornment>
                    ),
                  }}
                />
                {errors?.street && <ErrorMessage error={errors?.street} />}
              </div>

              <div>
                <TextField
                  id="input-number"
                  fullWidth
                  disabled
                  value={values?.number}
                  onChange={(e) => {
                    const maskedNumber = maskAddressNumber(e.target.value);
                    setFieldValue('number', maskedNumber);
                  }}
                  label="Número"
                  placeholder="376"
                  error={!!errors?.number}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdNumbers />
                      </InputAdornment>
                    ),
                  }}
                />
                {errors?.number && <ErrorMessage error={errors?.number} />}
              </div>

              <div>
                <TextField
                  id="input-complement"
                  fullWidth
                  disabled
                  value={values?.complement}
                  onChange={handleChange('complement')}
                  label="Complemento"
                  placeholder="-"
                  error={!!errors?.complement}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdLocationCity />
                      </InputAdornment>
                    ),
                  }}
                />
                {errors?.complement && <ErrorMessage error={errors?.complement} />}
              </div>

              {/* <div>
                <TextField
                  id="input-phone-2"
                  fullWidth
                  value={values?.secondPhone}
                  error={!!errors?.secondPhone}
                  label="Telefone 2 com DDD"
                  placeholder="(31) 9 9999-9999"
                  onChange={(e) => {
                    const maskedPhoneNumber = maskPhoneNumber(e.target.value);
                    setFieldValue('secondPhone', maskedPhoneNumber);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdPhone />
                      </InputAdornment>
                    ),
                  }}
                />

                {errors?.secondPhone && <ErrorMessage error={errors?.secondPhone} />}
              </div> */}

              {/* <Span2 className="d-grid gap-2">
                <Button color="primary" type="submit" variant="contained" disabled={isSubmitting}>
                  {!isSubmitting ? 'EDITAR' : <CircularProgress color="inherit" size={24} />}
                </Button>
              </Span2> */}
            </NeedsValidationGrid>
          </CardContent>
        </Card>
      </Box>

      {isFechingProfileData && <LoadingAnimation />}
    </>
  );
};

export default Profile;
